import { Component, EventEmitter, Input, OnChanges, OnInit, Output, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { FieldTypeEnum, MatTableColumnModel, MatTableInputTypeEnum, TableActionButtons } from './model';
import { LocalStorageService } from '../../services/storage/local-storage.service';
import { TranslateService } from '@ngx-translate/core';
import { LocalDatePipe } from '../../pipes/local-date.pipe';
import { ContractWorkModel } from '../../models';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { isNotNullOrUndefined } from 'codelyzer/util/isNotNullOrUndefined';

@Component({
  selector: 'sc-mat-table',
  templateUrl: './mat-table.component.html',
  styleUrls: ['./mat-table.component.scss']
})
export class MatTableComponent implements OnInit, OnChanges {
  @Input() public columnsDetails: MatTableColumnModel[];
  @Input() public dataSource: MatTableDataSource<any>;
  @Input() public withPaginator = false;
  @Input() public pageSize = 100;
  @Input() public pageSizeOptions = [5, 10, 20, 100];

  @Input() public withFilter = false;
  @Input() public clickableRow = false;
  @Input() public isInTableConsistency = false;
  @Input() public applyStyleForMsgCtrl = false;

  @Input() public formGroup: FormGroup = new FormBuilder().group({});
  @Input() public form = false;
  @Input() public withCheckBoxes = false;
  @Input() public hoverRow = false;
  @Input() public showTooltipOnColumn = false;
  @Input() public isSlicedValueAccepted = true;
  @Input() public keepHoverOnClickedRow = false;
  @Input() public clickRightEnabled = false;
  @Input() public slideToggleEnabled = true;
  @Input() public enableRedClearIconOnCheckBox = false;
  @Input() public enableRedCheckboxColumn = false;
  @Input() public ordered = false;
  @Input() public showProgressBar = false;

  // tableName isUsed to retrieve the associated filters, if no name is given the default localFilters is loaded
  @Input() public tableName = '';
  @Input() public maxHeight = '60vh';
  @Input() public checkboxFlexGrow = 1;

  // The prefix is needed to correctly distinct controls and attributes
  @Input() public prefix: string;
  @Input() public tableActionButtons = new TableActionButtons(
    false,
    false,
    false,
    false,
    false
  );

  @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;
  @ViewChild(MatSort, {static: true}) sort: MatSort;

  // out put called when clicking on row
  @Output() public clickRowEvent = new EventEmitter();
  // output called when clicking on the + button
  @Output() public addEmitter = new EventEmitter();
  // output called to load the suggestions of a auto complete field
  @Output() public autoCompleteEmitter = new EventEmitter();
  // output called to load the suggestions of a auto complete field when clicking on the field
  @Output() public autoCompleteClickEmitter = new EventEmitter();
  // output called when a suggestion is selected in a auto complete field
  @Output() public autoCompleteOnSelectEmitter = new EventEmitter();
  // output called when removing an item
  @Output() public removeEmitter = new EventEmitter();
  // output called when selecting a value
  @Output() public selectEvent = new EventEmitter();
  // output called when clicking on the Search button
  @Output() public searchEmitter = new EventEmitter();
  // output called when distributing amounts
  @Output() public calculateEmitter = new EventEmitter();
  // output called when importing values
  @Output() public importEmitter = new EventEmitter();
  // output called when clicking on the Search right holder button
  @Output() public searchRightHolderEmitter = new EventEmitter();
  // output called when clicking on the Consult message control link
  @Output() public consultMessageControlEmitter = new EventEmitter();
  @Output() public getListForSimpleAutoEmitter = new EventEmitter();
  @Output() public selectedItemEmitter = new EventEmitter();
  @Output() public checkedItemEmitter = new EventEmitter();
  @Output() public updatedValueEmitter = new EventEmitter();
  @Output() public clickEditEmitter = new EventEmitter();
  @Output() public duplicateEmitter = new EventEmitter();
  @Output() public protectEmitter = new EventEmitter();
  // Send textual filter elements
  @Output() public getFilters = new EventEmitter();
  // send mouse event to handle right click
  @Output() public contextMenuEvent: EventEmitter<any> = new EventEmitter();
  // output called when removing an item of a mat chip
  @Output() public matChipRemoveEmitter = new EventEmitter();
  // handle slide toggle event
  @Output() public slideToggleEmitter: EventEmitter<boolean> = new EventEmitter();
  // called when removed manually text from input
  @Output() public autoCompleteOnChangesDetected = new EventEmitter();
  public columnNames: string[] = [];
  public translatedColumnNames: string[] = [];
  selectAllItems: boolean = false;
  @Input() public selectedItems: any[] = [];
  public filters: string[] = [];

  constructor(
    private localStorage: LocalStorageService,
    private datePipe: DatePipe,
    private localDatePipe: LocalDatePipe,
    public translateService: TranslateService
  ) {
  }

  public ngOnInit() {

    this.initColumns();

    this.initData();
  }

  public ngOnChanges(event) {
    if (event.dataSource && !event.dataSource.firstChange &&  isNotNullOrUndefined(this.dataSource.data) && this.dataSource.data.length) {
      this.initData();
      // creating custom form control for select field single model
      this.columnsDetails.forEach(col => {
        if (col.fieldType === FieldTypeEnum.MODEL && (col.inputType === MatTableInputTypeEnum.SELECT_FIELD ||
          col.inputType === MatTableInputTypeEnum.SELECT_EVENT || col.inputType === MatTableInputTypeEnum.SELECT_ID)) {
          if (!isNotNullOrUndefined(this.formGroup.get(this.prefix + '-' + col.field + 'Single' + this.dataSource.data[this.dataSource.data.length - 1].uuid))) {
            this.formGroup.setControl(this.prefix + '-' + col.field + 'Single' + this.dataSource.data[this.dataSource.data.length - 1].uuid,
              this.formGroup.controls[this.prefix + '-' + col.field + this.dataSource.data[this.dataSource.data.length - 1].uuid]);
            this.formGroup.removeControl(this.prefix + '-' + col.field + this.dataSource.data[this.dataSource.data.length - 1].uuid);
          }
        }
      });

    } else if (event.columnsDetails && !event.columnsDetails.firstChange) {
      this.initColumns();
    }
    if (isNotNullOrUndefined(event.selectedItems)) {
      if ( event.selectedItems.currentValue.length === 0) {
        this.selectAllItems = false;
      }
    }
  }

  public onRowClick(item, event) {
    if (this.keepHoverOnClickedRow) {
      // if keepHoverOnClick is true, then we want to keep selected row active and colored with class: selected-row
      // .selectedrow should be setted on CSS according to your needs on your module...we may set a default here (im not good with colors)
      const matTable = document.getElementsByClassName('selected-row');
      // Remove all old selected rows if exist
      for (let matTableKey = 0;  isNotNullOrUndefined(matTable) && matTableKey < matTable.length; matTableKey++) {
        matTable[matTableKey].classList.remove('selected-row');
      }
      // set .selected-row on our row
      if ( isNotNullOrUndefined(event.target.parentElement)) {
        if ( isNotNullOrUndefined(event.target.parentElement.classList) && event.target.parentElement.classList.length > 0) {
          // if clicked on text, the mat cell would be coloried so we have to find the parentNode (mat-row)
          if (event.target.parentElement.classList[0] === 'mat-cell') {
            if ( isNotNullOrUndefined(event.target.parentElement.parentElement)) {
              event.target.parentElement.parentElement.classList.add('selected-row');
            }
          } else {
              // normal case, we color the clicked row
              if (event.target.parentElement.classList.contains('mat-row')) {
                event.target.parentElement.classList.add('selected-row');
              } else {
                // anormal case: if we click in the extremity of a row
                if ( isNotNullOrUndefined(event.target)) {
                  event.target.classList.add('selected-row');
                }
              }
            }
          }
        }
    }
    if (
      this.clickableRow && !event.altKey &&
      // prevents bugs when checkbox is present
      ( isNotNullOrUndefined(event.srcElement.firstChild.classList)
        ? event.srcElement.firstChild.classList[0] !== 'mat-checkbox-input'
        : true
      )) {
      this.clickRowEvent.emit(item);
    }
  }

  public sendContextMenu(item, event) {
    if (this.clickRightEnabled) {
      // didn't contactinated clickRightEnabled with the next condition for buggy condition reason.
      if (
        this.clickableRow &&
        // prevents bugs when checkbox is present
         isNotNullOrUndefined(event.srcElement.firstChild.classList)
          ? event.srcElement.firstChild.classList[0] !== 'mat-checkbox-input'
          : true
      ) {
        console.log('context menu sent');
        this.contextMenuEvent.emit({item, event});
      }
    }
  }

  public onAddClick() {
    this.addEmitter.emit();
  }

  public onProtectClick() {
    this.protectEmitter.emit();
  }

  public onDuplicateClick() {
    this.duplicateEmitter.emit();
  }

  public onEditClick() {
    this.clickEditEmitter.emit();
  }

  public search(event, column: string, index) {
    this.autoCompleteEmitter.emit({event, column, index});
  }

  public delete(item, index) {
    this.matChipRemoveEmitter.emit({item, index});
  }

  public autoCompleteClick(event, column: string, index) {
    this.autoCompleteClickEmitter.emit({event, column, index});
  }

  public onSelect(event, index, column, columnIndex) {
    this.columnsDetails[columnIndex].suggestions = [];
    this.autoCompleteOnSelectEmitter.emit({event, index, column});
  }

  public remove(q: string, chipList): void {
    const index = chipList.indexOf(q);

    if (index >= 0) {
      chipList.splice(index, 1);
    }
  }

  /**
   * Manage checkbox for each item
   * @param item: table item
   */
  public selectOrUnselectItem(item): void {
    if (item.checked) {
      this.addItemToSelectedList(item);
    } else {
      this.selectAllItems = false;
      this.removeItemFromSelectedList(item);
    }
  }

  /**
   * addItemToSelectedList
   * @param item: table item
   */
  public addItemToSelectedList(item): void {
    const index = this.selectedItems.indexOf(item);
    if (index === -1) {
      this.selectedItems.push(item);
      this.onSelectedItemsChange();
    }
  }

  /**
   * removeItemFromSelectedList
   * @param item: table item
   */
  public removeItemFromSelectedList(item): void {
    const index = this.selectedItems.indexOf(item);
    if (index !== -1) {
      this.selectedItems.splice(index, 1);
      this.onSelectedItemsChange();
    }
  }

  /**
   * Manage "checkbox all" button
   */
  public selectAllItem(): void {
    this.dataSource.data.forEach(item => {
      if (this.selectAllItems) {
        this.addItemToSelectedList(item);
        item.checked = true;
      } else {
        this.removeItemFromSelectedList(item);
        item.checked = false;
      }
    });
  }

  public removeItem(): void {
    const itemToRemoveFromSelection = [];
    // For each selected insert...
    this.selectedItems.forEach(item => {
      const index = this.dataSource.data.indexOf(item);
      if (index !== -1) {
        this.dataSource.data.splice(index, 1);
        itemToRemoveFromSelection.push(item);
        this.columnsDetails.forEach((column, j) => {
          if (column.fieldType === FieldTypeEnum.MODEL && (column.inputType === MatTableInputTypeEnum.SELECT_FIELD || column.inputType === MatTableInputTypeEnum.SELECT_EVENT)
            || (column.fieldType === FieldTypeEnum.SUB_MODEL && (column.inputType === MatTableInputTypeEnum.AUTO_COMPLETE || column.inputType === MatTableInputTypeEnum.SIMPLE_AUTOCOMPLETE))) {
            this.formGroup.removeControl(this.prefix + '-' + this.columnNames[j] + item.uuid);
          } else {
            this.formGroup.removeControl(this.prefix + '-' + column.field + item.uuid
            );
          }
        });
      }
    });
    itemToRemoveFromSelection.forEach(item => this.removeItemFromSelectedList(item));
    this.selectAllItems = false;
    this.dataSource = new MatTableDataSource(this.dataSource.data);
    this.removeEmitter.emit(itemToRemoveFromSelection);
  }

  public onSelectOption(event, column, index) {
    this.selectEvent.emit({event, column, index});
  }

  public getListForSimpleAuto(event, i: number, column: MatTableColumnModel) {
    column.filteredList = null;
    this.getListForSimpleAutoEmitter.emit({index: i, columnValue: column});
  }

  public onSearchClick() {
    this.searchEmitter.emit();
  }

  public onCalculateClick() {
    this.calculateEmitter.emit();
  }

  public onImportClick() {
    this.importEmitter.emit();
  }

  public onSelectedItemsChange() {
    this.selectedItemEmitter.emit(this.selectedItems);
  }

  public onCheckedColumn(item) {
    this.checkedItemEmitter.emit(item);
  }

  public onSearchRightHolderClick(item, event) {
    event.stopPropagation();
    this.searchRightHolderEmitter.emit(item);
  }

  public onConsultMessageControlClick(item) {
    event.stopPropagation();
    this.consultMessageControlEmitter.emit(item);
  }

  /**
   * Emit the new Value
   * @param event event
   * @param uuid uuid
   * @param column column
   */
  public onUpdateValue(event, uuid, column) {
    this.updatedValueEmitter.emit({event, uuid, column});
  }

  public filter(event, column) {
    const filter = [];
    this.columnsDetails.forEach(col =>
      filter.push([col.field, col.filterValue])
    );
    this.dataSource.filter = JSON.stringify(filter);
    this.getFilters.emit(this.dataSource.filter);
    if (this.dataSource.filter !== '[]') {
      this.localStorage.post(
        'localFilters' + this.tableName,
        this.dataSource.filter
      );
    } else {
      this.localStorage.delete('localFilters' + this.tableName);
    }
  }

  private createDisplayValue() {
    if ( isNotNullOrUndefined(this.dataSource.data)) {
      this.dataSource.data.forEach(item => {
        this.columnsDetails.forEach((column, index) => {
          switch (column.fieldType) {
            case FieldTypeEnum.MODEL:
              if ( isNotNullOrUndefined(item[column.field]) &&
                isNaN(item[column.field]) &&
                item[column.field].length > 60) {
                item[column.field] = item[column.field].trim();
                if (column.inputType !== MatTableInputTypeEnum.CUSTOM && this.isSlicedValueAccepted) {
                  item[column.field] = item[column.field].slice(0, 60) + '...';
                }
              }
              item[this.columnNames[index]] = {
                matTableValue:  isNotNullOrUndefined(item[column.field])
                  ? item[column.field].toString()
                  : // prevents error of dateFormat pipe if null value
                  column.inputType === MatTableInputTypeEnum.DATE_FIELD ||
                   isNotNullOrUndefined(column.dateFormat)
                    ? ''
                    : '-'
              };
              if (
                 isNotNullOrUndefined(column.inputType) &&
                column.inputType === MatTableInputTypeEnum.SELECT_FIELD
              ) {
                column.selectValues.forEach(value => {
                  if (
                     isNotNullOrUndefined(item[column.field]) &&
                    item[column.field] === value.id
                  ) {
                    item[this.columnNames[index]] = value;
                  }
                });
              }
              break;
            case FieldTypeEnum.SUB_MODEL:
              if (
                 isNotNullOrUndefined(item[column.field]) &&
                 isNotNullOrUndefined(item[column.field][column.subField]) &&
                isNaN(item[column.field][column.subField]) &&
                item[column.field][column.subField].length > 60
              ) {
                item[column.field][column.subField] = item[column.field][
                  column.subField
                  ].trim();
                item[column.field][column.subField] =
                  item[column.field][column.subField].slice(0, 60) + '...';
              }
              item[this.columnNames[index]] = {
                matTableValue:  isNotNullOrUndefined(item[column.field])
                  ?  isNotNullOrUndefined(item[column.field][column.subField])
                    ? item[column.field][column.subField].toString()
                    : '-'
                  : '-'
              };
              if (
                 isNotNullOrUndefined(column.inputType) &&
                column.inputType === MatTableInputTypeEnum.SELECT_FIELD
              ) {
                column.selectValues.forEach(value => {
                  if (
                     isNotNullOrUndefined(item[column.field]) &&
                    item[column.field].id === value.id
                  ) {
                    item[column.field] = value;
                  }
                });
              }
              break;

            case FieldTypeEnum.SUB_SUB_MODEL:
              if (
                 isNotNullOrUndefined(item[column.field]) &&
                 isNotNullOrUndefined(item[column.field][column.subField]) &&
                 isNotNullOrUndefined(
                  item[column.field][column.subField][column.subSubField]
                ) &&
                isNaN(
                  item[column.field][column.subField][column.subSubField]
                ) &&
                item[column.field][column.subField][column.subSubField].length >
                60
              ) {
                item[column.field][column.subField][column.subSubField] = item[
                  column.field
                  ][column.subField][column.subSubField].trim();
                item[column.field][column.subField][column.subSubField] =
                  item[column.field][column.subField][column.subSubField].slice(
                    0,
                    60
                  ) + '...';
              }
              item[this.columnNames[index]] = {
                matTableValue:  isNotNullOrUndefined(item[column.field])
                  ?  isNotNullOrUndefined(item[column.field][column.subField])
                    ?  isNotNullOrUndefined(
                      item[column.field][column.subField][column.subSubField]
                    )
                      ? item[column.field][column.subField][
                        column.subSubField
                        ].toString()
                      : '-'
                    : '-'
                  : '-'
              };
              if (
                 isNotNullOrUndefined(column.inputType) &&
                column.inputType === MatTableInputTypeEnum.SELECT_FIELD
              ) {
                column.selectValues.forEach(value => {
                  if (
                     isNotNullOrUndefined(item[column.field][column.subField]) &&
                    item[column.field][column.subField].id === value.id
                  ) {
                    item[column.field][column.subField] = value;
                  }
                });
              }
              break;
            case FieldTypeEnum.SUB_SUB_SUB_MODEL:
              if (
                 isNotNullOrUndefined(item[column.field]) &&
                 isNotNullOrUndefined(item[column.field][column.subField]) &&
                 isNotNullOrUndefined(
                  item[column.field][column.subField][column.subSubField]
                ) &&
                 isNotNullOrUndefined(
                  item[column.field][column.subField][column.subSubField][
                    column.subSubSubField
                    ]
                ) &&
                isNaN(
                  item[column.field][column.subField][column.subSubField][
                    column.subSubSubField
                    ]
                ) &&
                item[column.field][column.subField][column.subSubField][
                  column.subSubSubField
                  ].length > 60
              ) {
                item[column.field][column.subField][column.subSubField][
                  column.subSubSubField
                  ] = item[column.field][column.subField][column.subSubField][
                  column.subSubSubField
                  ].trim();
                item[column.field][column.subField][column.subSubField][
                  column.subSubSubField
                  ] =
                  item[column.field][column.subField][column.subSubField][
                    column.subSubSubField
                    ].slice(0, 60) + '...';
              }
              item[this.columnNames[index]] = {
                matTableValue:  isNotNullOrUndefined(item[column.field])
                  ?  isNotNullOrUndefined(item[column.field][column.subField])
                    ?  isNotNullOrUndefined(
                      item[column.field][column.subField][column.subSubField]
                    )
                      ?  isNotNullOrUndefined(
                        item[column.field][column.subField][
                          column.subSubField
                          ][column.subSubSubField]
                      )
                        ? item[column.field][column.subField][
                          column.subSubField
                          ][column.subSubSubField].toString()
                        : '-'
                      : '-'
                    : '-'
                  : '-'
              };
              if (
                 isNotNullOrUndefined(column.inputType) &&
                column.inputType === MatTableInputTypeEnum.SELECT_FIELD
              ) {
                column.selectValues.forEach(value => {
                  if (
                     isNotNullOrUndefined(
                      item[column.field][column.subField][column.subSubField]
                    ) &&
                    item[column.field][column.subField][column.subSubField]
                      .id === value.id
                  ) {
                    item[column.field][column.subField][
                      column.subSubField
                      ] = value;
                  }
                });
              }
              break;
          }
          if (
            column.currencyType &&
            item[this.columnNames[index]].matTableValue === '-'
          ) {
            item[this.columnNames[index]].matTableValue = 0;
          }
          if (
             isNotNullOrUndefined(column.inputType) &&
            column.inputType === MatTableInputTypeEnum.SIMPLE_AUTOCOMPLETE
          ) {
            column.filteredList = column.selectValues;
          }
        });
      });
    }
  }

  private initData() {
    if (this.withPaginator) {
      this.dataSource.paginator = this.paginator;
    }
    // creating field to display value with association with the newly created keys
    this.createDisplayValue();

    // Makes the sorter case insensitive
    this.dataSource.sort = this.sort;
    this.dataSource.sortingDataAccessor = (data, sortHeaderId) => {

      const format = data[sortHeaderId].matTableValue.replace('/', 'a').replace('-', 'a');
      const checkNumberAndDate = isNaN(format);
      if (!checkNumberAndDate) {
        return  isNotNullOrUndefined(data[sortHeaderId].matTableValue)
          ? +data[sortHeaderId].matTableValue
          : '';
      } else {
        return  isNotNullOrUndefined(data[sortHeaderId].matTableValue)
          ? data[sortHeaderId].matTableValue.toLowerCase()
          : '';
      }
    };
    if (this.form) {
      // Makes the sorter case insensitive

      this.dataSource.data.forEach(data => {
        this.columnsDetails.forEach((column, j) => {
          if ( isNotNullOrUndefined(column.field)) {
            if (
              column.fieldType === FieldTypeEnum.MODEL &&
              (column.inputType === MatTableInputTypeEnum.SELECT_FIELD ||
                column.inputType === MatTableInputTypeEnum.SELECT_EVENT ||
                column.inputType === MatTableInputTypeEnum.SELECT_ID)
            ) {
              this.formGroup.addControl(
                this.prefix + '-' + this.columnNames[j] + data.uuid,
                new FormControl(data[j])
              );
              this.formGroup.removeControl(
                this.prefix + '-' + column.field + data.uuid
              );
            }
          }
        });
      });
    }

    // Custom case insensitive filter
    if (this.withFilter) {
      this.dataSource.filterPredicate = (data) => {
        let isMatch = true;
        this.columnsDetails.forEach(col => {
          if (isMatch === true && col.withFilter === true) {
            if (col.fieldType === FieldTypeEnum.MODEL) {
              if (col.dateFormat) {
                isMatch = (data[col.field] ? this.datePipe.transform(data[col.field], col.dateFormat) : '').toString().toLowerCase().includes(col.filterValue.toString().toLowerCase());
              } else {
                if (col.isListFilter) {
                  let isMatchList = false;
                  if ( isNotNullOrUndefined(data[col.field])) {
                    data[col.field].forEach(value => {
                      isMatch = (value[col.fieldToDisplay] ? value[col.fieldToDisplay] : '').toString().toLowerCase().includes(col.filterValue.toString().toLowerCase());
                      if (isMatch === true) {
                        isMatchList = isMatch;
                      }
                    });
                    isMatch = isMatchList;
                  }
                } else {
                  isMatch = (data[col.field] ? data[col.field] : '').toString().toLowerCase().includes(col.filterValue.toString().toLowerCase());
                }
              }
            } else if (col.fieldType === FieldTypeEnum.SUB_MODEL && data[col.field]) {
              isMatch = (data[col.field][col.subField] ? data[col.field][col.subField] : '').toString().toLowerCase().includes(col.filterValue.toString().toLowerCase());
            } else if (col.fieldType === FieldTypeEnum.SUB_SUB_MODEL && data[col.field][col.subField]) {
              isMatch = (data[col.field][col.subField][col.subSubField] ? data[col.field][col.subField][col.subSubField] : '').toString().toLowerCase().includes(col.filterValue.toString().toLowerCase());
            } else if (col.fieldType === FieldTypeEnum.SUB_SUB_SUB_MODEL && data[col.field][col.subField][col.subSubField]) {
              isMatch = (data[col.field][col.subField][col.subSubField][col.subSubSubField] ? data[col.field][col.subField][col.subSubField][col.subSubSubField] : '')
                .toString().toLowerCase().includes(col.filterValue.toString().toLowerCase());
            }
          }
        });
        return isMatch;
      };

      // load previous filters
      const localFilters = this.localStorage.get(
        'localFilters' + this.tableName
      );
      if (localFilters && localFilters.length > 0) {
        const localFiltersparse: [] = JSON.parse(localFilters);
        this.columnsDetails.forEach(col => col.filterValue = localFiltersparse.find(filter => filter[0] === col.field)[1]);
        this.dataSource.filter = localFilters;
      }
    }
  }

  private initColumns() {
    this.columnNames = [];
    this.translatedColumnNames = [];
    if (this.withCheckBoxes) {
      this.columnNames.unshift('checkBoxColumn');
      this.columnsDetails.unshift(
        new MatTableColumnModel(null, '', null, null)
      );
    }
    // Creating column keys by the concatenation of all the fields in order not to erase the source data
    this.columnsDetails.forEach(column => {
      this.translatedColumnNames.push(
         isNotNullOrUndefined(column.label) ? column.label : ''
      );
      switch (column.fieldType) {
        case FieldTypeEnum.MODEL:
          this.columnNames.push(column.field + 'Single');
          break;
        case FieldTypeEnum.SUB_MODEL:
          this.columnNames.push(column.field + column.subField);
          break;
        case FieldTypeEnum.SUB_SUB_MODEL:
          this.columnNames.push(
            column.field + column.subField + column.subSubField
          );
          break;
        case FieldTypeEnum.SUB_SUB_SUB_MODEL:
          this.columnNames.push(
            column.field +
            column.subField +
            column.subSubField +
            column.subSubSubField
          );
          break;
      }
    });
  }

  public compareWith(object1, object2) {
    return object1 && object2 ? object1.id === object2.id : object1 === object2;
  }

  handleSwitch(slidePosition: MatSlideToggleChange) {
    this.slideToggleEnabled = slidePosition.checked;
    this.slideToggleEmitter.emit(slidePosition.checked);
  }

  changesEmit($event: any, field: string, uuid: any) {
    this.autoCompleteOnChangesDetected.emit({$event, field, uuid});
  }

}
